import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dateUtils from '../../Utils/dateUtils';
import Modal from "../Modal";

function MigrationList({ migrations }) {
    const { t } = useTranslation();
    const [modalView, setModalView] = useState(null);
    const [selectedMigration, setSelectedMigration] = useState(null);

    const sortedMigrations = useMemo(() => {
        return [...migrations].sort((a, b) => new Date(b.created) - new Date(a.created));
    }, [migrations]);

    const showModal = (view, migration) => () => {
        setSelectedMigration(migration);
        setModalView(view);
    };

    const closeModal = () => setModalView(null);

    const modalTitle = modalView === 'history' ? t('migrations.statusHistory') : t('migrations.errorLogs');
    const modalProps = {
        title: modalTitle,
        onCancelClick: closeModal,
        stateful: true,
        open: true,
        showButtons: false,
        customClassName: 'modal-lg',
    };

    const getStatusClass = (statusCode) => {
        switch (statusCode) {
            case 0: return '';
            case 1: return 'text-primary';
            case 2: return 'text-success';
            case 3: return 'text-danger';
            default: return '';
        }
    };

    return (
        <div className="col-xs-12" style={{ overflowX: 'auto', maxWidth: '100%', width: 0, minWidth: '100%' }}>
            <table className="table table-stripe" style={{ whiteSpace: 'nowrap' }}>
                <thead>
                    <tr>
                        <th className="text-left">{t('migrations.migrationType')}</th>
                        <th className="text-left">{t('migrations.sourceType')}</th>
                        <th className="text-left">{t('migrations.status')}</th>
                        <th className="text-left">{t('migrations.eAccountingGuideState')}</th>
                        <th className="text-left">{t('migrations.created')}</th>
                        <th className="text-left">{t('migrations.started')}</th>
                        <th className="text-left">{t('migrations.done')}</th>
                        <th className="text-left">{t('migrations.changed')}</th>
                        <th className="text-center" style={{ width: 80 }}></th>
                        <th className="text-center" style={{ width: 140 }}></th>
                        <th className="text-center" style={{ width: 120 }}></th>
                    </tr>
                </thead>
                <tbody>
                    {sortedMigrations.map((migration) => (
                        <tr key={migration.migrationId}>
                            <td>{migration.migrationType?.name}</td>
                            <td>{migration.sourceType?.name}</td>
                            <td className={getStatusClass(migration.status?.code)}>{migration.status?.name}</td>
                            <td>{migration.eAccountingGuideState?.name}</td>
                            <td>{dateUtils.formatDateAndTime(migration.created)}</td>
                            <td>{migration.startedAt ? dateUtils.formatDateAndTime(migration.startedAt) : ''}</td>
                            <td>{migration.doneAt ? dateUtils.formatDateAndTime(migration.doneAt) : ''}</td>
                            <td>{dateUtils.formatDateAndTime(migration.changed)}</td>
                            <td className="text-center">
                                {migration.detailsUrl && (
                                    <a href={migration.detailsUrl} target="_blank" rel="noopener noreferrer" className="btn btn-default btn-sm">
                                        <span className="vismaicon vismaicon-sm vismaicon-export" /> {t('migrations.details')}
                                    </a>
                                )}
                            </td>
                            <td className="text-center">
                                {migration.statusHistory?.length > 0 && (
                                    <button className="btn btn-default" onClick={showModal('history', migration)}>
                                        <span className="vismaicon vismaicon-sm vismaicon-history" /> {t('migrations.statusHistory')} ({migration.statusHistory.length})
                                    </button>
                                )}
                            </td>
                            <td className="text-center">
                                {migration.errorLogs?.length > 0 && (
                                    <button className="btn btn-default" onClick={showModal('errors', migration)}>
                                        <span className="vismaicon vismaicon-sm vismaicon-filled vismaicon-error" /> {t('migrations.errors')} ({migration.errorLogs.length})
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {modalView === 'errors' && selectedMigration && (
                <Modal {...modalProps}>
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>{t('migrations.errorDate')}</th>
                                <th>{t('migrations.errorStatus')}</th>
                                <th>{t('migrations.errorItem')}</th>
                                <th>{t('migrations.errorMessage')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedMigration.errorLogs.map((log, index) => (
                                <tr key={index}>
                                    <td className="text-nowrap">{dateUtils.formatDateAndTime(log.dateTime)}</td>
                                    <td>{log.occuredOnStatus?.name}</td>
                                    <td>{log.itemName}</td>
                                    <td>{log.message}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Modal>
            )}

            {modalView === 'history' && selectedMigration && (
                <Modal {...modalProps}>
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>{t('migrations.historyTime')}</th>
                                <th>{t('migrations.historyStatus')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...selectedMigration.statusHistory]
                                .sort((a, b) => new Date(b.timeOfChange) - new Date(a.timeOfChange))
                                .map((entry, index) => (
                                    <tr key={index}>
                                        <td className="text-nowrap">{dateUtils.formatDateAndTime(entry.timeOfChange)}</td>
                                        <td className={getStatusClass(entry.status?.code)}>{entry.status?.name}</td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </Modal>
            )}
        </div>
    );
}

export default MigrationList;
